import React from 'react';
import {withSystem, withMeta, Plugins} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
} from '@packages/utilities/contextual-expression';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TEnum,
  TText,
  TRecord,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {GetAddressInput} from './GetAddressInput';
import {MapApiKey} from '../MapApiKeyType';

import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const {getIn} = ContextualExpressions;

const exprFromRecord = (
  expr: ContextualExpression,
  property: string,
  nestedProperty: string
) => {
  return getIn(expr, [
    {in: 'Record', at: property},
    {in: 'Record', at: nestedProperty},
  ]);
};
const selectGoogleMapsApiKey = (state: any) =>
  state.SiteDataStore.googleMapsApiKey;

const Separator = emotionStyled('div')({
  width: '100%',
  height: tokens.space[2],
});

const DisabledBox = emotionStyled('div')({
  position: 'absolute',
  opacity: 0.5,
  backgroundColor: tokens.colors.background1,
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  cursor: 'not-allowed',
});

const MapWidgetDataType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    widget: TRecord({
      type: withSystem({defaultValue: 'map', private: true}, TText),
      address: withSystem({private: true, isLocalizable: true}, TText),
    }),
    attr: TRecord({
      role: TText,
      'data-widget-latlng': withSystem({private: true}, TText),
      title: withMeta(
        {placeholder: 'e.g. Business headquarters'},
        withSystem({label: 'Title', isLocalizable: true}, TText)
      ),
      'data-widget-tooltip': withMeta(
        {placeholder: 'e.g. Headquarters'},
        withSystem({label: 'Tooltip', isLocalizable: true}, TText)
      ),
      'data-widget-zoom': withSystem(
        {defaultValue: 12, label: 'Zoom', isLocalizable: true},
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Slider')))
      ),
      'data-widget-style': withSystem(
        {
          defaultValue: 'roadmap',
          label: 'Map type',
          isFullWidth: true,
          isLocalizable: true,
        },
        TEnum({
          roadmap: 'Road',
          terrain: 'Terrain',
          satellite: 'Satellite',
          hybrid: 'Hybrid',
        })
      ),
      'data-enable-scroll': withSystem(
        {
          defaultValue: true,
          label: 'Scrollwheel zoom enabled',
          labelVariant: 'hidden',
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
        )
      ),
      'data-enable-touch': withSystem(
        {
          defaultValue: true,
          label: 'Touch drag enabled',
          labelVariant: 'hidden',
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
        )
      ),
    }),
  },

  render: function MapWidget({expr, dispatch}) {
    const hasValidKey = useDesignerSelector(selectGoogleMapsApiKey);
    const showKeyRequired = !hasValidKey;

    const [address, setAddress] = React.useState(
      exprFromRecord(expr, 'widget', 'address')
    );
    const [latLng] = React.useState(
      exprFromRecord(expr, 'attr', 'data-widget-latlng')
    );
    const [title, setTitle] = React.useState(
      exprFromRecord(expr, 'attr', 'title')
    );
    const [tooltip, setTooltip] = React.useState(
      exprFromRecord(expr, 'attr', 'data-widget-tooltip')
    );
    const [zoom, setZoom] = React.useState(
      exprFromRecord(expr, 'attr', 'data-widget-zoom')
    );
    const [mapType, setMapType] = React.useState(
      exprFromRecord(expr, 'attr', 'data-widget-style')
    );
    const [scrollwheel, setScrollwheel] = React.useState(
      exprFromRecord(expr, 'attr', 'data-enable-scroll')
    );
    const [touch, setTouch] = React.useState(
      exprFromRecord(expr, 'attr', 'data-enable-touch')
    );

    React.useEffect(() => {
      const nextAddress = exprFromRecord(expr, 'widget', 'address');
      if (nextAddress !== address) {
        setAddress(nextAddress);
      }
    }, [expr, address]);

    React.useEffect(() => {
      const nextTitle = exprFromRecord(expr, 'attr', 'title');
      if (nextTitle !== title) {
        setTitle(nextTitle);
      }
    }, [expr, title]);
    React.useEffect(() => {
      const nextTooltip = exprFromRecord(expr, 'attr', 'data-widget-tooltip');
      if (nextTooltip !== tooltip) {
        setTooltip(nextTooltip);
      }
    }, [expr, tooltip]);
    React.useEffect(() => {
      const nextZoom = exprFromRecord(expr, 'attr', 'data-widget-zoom');
      if (nextZoom !== zoom) {
        setZoom(nextZoom);
      }
    }, [expr, zoom]);
    React.useEffect(() => {
      const nextMapType = exprFromRecord(expr, 'attr', 'data-widget-style');
      if (nextMapType !== mapType) {
        setMapType(nextMapType);
      }
    }, [expr, mapType]);
    React.useEffect(() => {
      const nextScrollwheel = exprFromRecord(
        expr,
        'attr',
        'data-enable-scroll'
      );
      if (nextScrollwheel !== scrollwheel) {
        setScrollwheel(nextScrollwheel);
      }
    }, [expr, scrollwheel]);
    React.useEffect(() => {
      const nextTouch = exprFromRecord(expr, 'attr', 'data-enable-touch');
      if (nextTouch !== touch) {
        setTouch(nextTouch);
      }
    }, [expr, touch]);

    if (
      address === null ||
      address === undefined ||
      latLng === null ||
      latLng === undefined ||
      title === null ||
      title === undefined ||
      tooltip === null ||
      tooltip === undefined ||
      zoom === null ||
      zoom === undefined ||
      mapType === null ||
      mapType === undefined ||
      scrollwheel === null ||
      scrollwheel === undefined ||
      touch === null ||
      touch === undefined
    )
      return null;

    const onChange = (property: string, nestedProperty: string) => {
      return extendExpressionEditorActionAtPath(dispatch)([
        {in: 'Record', at: property},
        {in: 'Record', at: nestedProperty},
      ]);
    };

    return (
      <>
        <MapApiKey />
        <div style={{position: 'relative'}}>
          <GetAddressInput
            address={address}
            dispatch={dispatch}
            contextualExpr={expr}
          />
          <ExpressionEditor
            expression={title}
            dispatch={onChange('attr', 'title')}
          />
          <Separator />
          <ExpressionEditor
            expression={tooltip}
            dispatch={onChange('attr', 'data-widget-tooltip')}
          />
          <Separator />
          <ExpressionEditor
            expression={zoom}
            dispatch={onChange('attr', 'data-widget-zoom')}
          />
          <ExpressionEditor
            expression={mapType}
            dispatch={onChange('attr', 'data-widget-style')}
          />
          <Separator />
          <ExpressionEditor
            expression={scrollwheel}
            dispatch={onChange('attr', 'data-enable-scroll')}
          />
          <ExpressionEditor
            expression={touch}
            dispatch={onChange('attr', 'data-enable-touch')}
          />
          {showKeyRequired ? <DisabledBox /> : null}
        </div>
      </>
    );
  },
});

export default MapWidgetDataType;
